import { workoutDisplayDigits as displayDigits } from '../../../utils/workoutNumberFormat';
import { normalizeInputDigits } from '../../../utils/localizedInput';
import { WorkoutDurationPicker } from '../../../components/WorkoutDurationPicker';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface WfComparisonRow {
  rowKey: string;
  planned?: string | number | null;
  done?: string | number | null;
  onChangePlanned?: (v: string) => void;
  onBlurPlanned?: () => void;
  onChangeDone?: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'decimal' | 'numeric';
}

export interface WfStatsRow {
  rowKey: string;
  caption: string;
  label: string;
  min?: string | number | null;
  avg?: string | number | null;
  max?: string | number | null;
  onChangeMin?: (v: string) => void;
  onChangeAvg?: (v: string) => void;
  onChangeMax?: (v: string) => void;
  onFocusMin?: () => void;
  onFocusAvg?: () => void;
  onFocusMax?: () => void;
  onBlurMin?: () => void;
  onBlurAvg?: () => void;
  onBlurMax?: () => void;
  placeholder?: string;
  inputMode?: 'text' | 'decimal' | 'numeric';
  rowClassName?: string;
}

const valOrDash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : String(v);

function EditableCell({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  inputMode = 'numeric',
  duration = false,
  label,
}: {
  value?: string | number | null;
  onChange?: (v: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  inputMode?: 'text' | 'decimal' | 'numeric';
  duration?: boolean;
  label?: string;
}) {
  const { i18n } = useTranslation();
  if (duration && onChange) return <WorkoutDurationPicker value={value} onChange={onChange} />;
  if (onChange) {
    return (
      <input
        aria-label={label}
        type="text"
        inputMode={inputMode}
        className="ws-table__input ltr-island"
        value={value === null || value === undefined ? '' : displayDigits(value, i18n.language)}
        onChange={e => onChange(normalizeInputDigits(e.target.value).replace(/٫/g, '.'))}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={displayDigits(placeholder ?? '—', i18n.language)}
      />
    );
  }
  return (
    <span className="ws-table__cell ltr-island">
      {displayDigits(valOrDash(value), i18n.language)}
    </span>
  );
}

/** Editable planned vs done table — mirrors Ai-Tempo ComparisonTable (showDelta=false). */
export function WfComparisonTable({ rows, plannedOnly = false }: { rows: WfComparisonRow[]; plannedOnly?: boolean }) {
  const { t } = useTranslation();
  if (!rows.length) return null;

  return (
    <div className="ws-section">
      <div className="ws-table">
        <div className={`ws-table__head ws-table__row ${plannedOnly ? 'ws-table__row--planned' : 'ws-table__row--3'}`}>
          <span />
          <span>{t('workoutForm.comparison.planned')}</span>
          {!plannedOnly && <span>{t('workoutForm.comparison.done')}</span>}
        </div>
        {rows.map((r, i) => (
          <div
            key={r.rowKey}
            className={`ws-table__row ${plannedOnly ? 'ws-table__row--planned' : 'ws-table__row--3'}${i < rows.length - 1 ? ' ws-table__row--border' : ''}`}
          >
            <span className="ws-table__label">{t(`workoutForm.metrics.${r.rowKey}`)}</span>
            <EditableCell
              duration={r.rowKey === 'time'}
              label={`${t(`workoutForm.metrics.${r.rowKey}`)} — ${t('workoutForm.comparison.planned')}`}
              value={r.planned}
              onChange={r.onChangePlanned}
              onBlur={r.onBlurPlanned}
              placeholder={r.placeholder}
              inputMode={r.inputMode}
            />
            {!plannedOnly && <EditableCell
              duration={r.rowKey === 'time'}
              label={`${t(`workoutForm.metrics.${r.rowKey}`)} — ${t('workoutForm.comparison.done')}`}
              value={r.done}
              onChange={r.onChangeDone}
              placeholder={r.placeholder}
              inputMode={r.inputMode}
            />}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Editable min/avg/max stats — mirrors Ai-Tempo StatsTable. */
export function WfStatsTable({ rows, showPace = true }: { rows: WfStatsRow[]; showPace?: boolean }) {
  const { t } = useTranslation();
  if (!rows.length) return null;

  return (
    <div className="ws-section">
      <div className="ws-section__head"><span className="ws-section__title">{t('workoutForm.stats.title')}</span></div>
      <div className="ws-table">
        <div className="ws-table__head ws-table__row ws-table__row--4">
          <span />
          <span>{t('workoutForm.stats.min')}</span>
          <span>{t('workoutForm.stats.avg')}</span>
          <span>{t('workoutForm.stats.max')}</span>
        </div>
        {rows.filter(row => showPace || row.rowKey !== 'pace').map((r, i) => (
          <div
            key={r.rowKey}
            className={`ws-table__row ws-table__row--4${i < rows.length - 1 ? ' ws-table__row--border' : ''}${r.rowClassName ? ` ${r.rowClassName}` : ''}`}
          >
            <span className="ws-table__label-col">
              <span className="ws-table__caption">{r.caption}</span>
              <span className="ws-table__unit ltr-island">{r.label}</span>
            </span>
            <EditableCell
              label={`${r.caption} — ${t('workoutForm.stats.min')}`}
              value={r.min}
              onChange={r.onChangeMin}
              onFocus={r.onFocusMin}
              onBlur={r.onBlurMin}
              placeholder={r.placeholder}
              inputMode={r.inputMode}
            />
            <EditableCell
              label={`${r.caption} — ${t('workoutForm.stats.avg')}`}
              value={r.avg}
              onChange={r.onChangeAvg}
              onFocus={r.onFocusAvg}
              onBlur={r.onBlurAvg}
              placeholder={r.placeholder}
              inputMode={r.inputMode}
            />
            <EditableCell
              label={`${r.caption} — ${t('workoutForm.stats.max')}`}
              value={r.max}
              onChange={r.onChangeMax}
              onFocus={r.onFocusMax}
              onBlur={r.onBlurMax}
              placeholder={r.placeholder}
              inputMode={r.inputMode}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
